<template>
  <div id="purchase-order-edit" class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue darken-2"
      v-if="loading"
    ></v-progress-linear>
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-form ref="poForm" v-model="valid" v-if="!loading">
      <v-row>
        <v-col cols="12">
          <v-card class="pa-5" flat>
            <HeaderDividerBlock
              headerText="Edit Purchase Order"
            ></HeaderDividerBlock>
            <v-row>
              <!-- Example fields, add your actual fields as needed -->
              <v-col cols="12" md="4">
                <v-select
                  v-model="formData.requisition_id"
                  :items="requisitions"
                  item-text="code"
                  item-value="id"
                  placeholder="Select Requisition"
                  dense
                  outlined
                  required
                  readonly
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Supplier</p>
                </v-col>
                <v-select
                  v-model="formData.supplier_code"
                  :items="suppliers"
                  item-text="name"
                  item-value="supplier_code"
                  placeholder="Select Supplier"
                  dense
                  outlined
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Expected Delivery Date</p>
                </v-col>
                <v-text-field
                  v-model="formData.expected_delivery_date"
                  placeholder="Expected Delivery Date"
                  type="date"
                  dense
                  outlined
                  required
                ></v-text-field>
              </v-col>
            </v-row>

            <v-col cols="12">
              <ItemsTable
                :items="formData.items"
                :products="products"
                @add-item="addItem"
                @remove-item="removeItem"
              />
            </v-col>

            <v-row class="mt-4">
              <v-col cols="12" md="2">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">PO Status</p>
                </v-col>
                <v-select
                  v-model="formData.status"
                  :items="['pending', 'cancelled', 'completed']"
                  placeholder="Select PO Status"
                  dense
                  outlined
                  required
                ></v-select>
              </v-col>
            </v-row>

            <v-row cols="12">
              <v-col></v-col>
              <v-col cols="12" md="4" sm="12">
                <v-col class="ma-0 pa-0">
                  <p class="text-h5 font-weight-bold">Summary</p>
                </v-col>
                <v-col class="px-7 pa-10 total-section">
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Sub Total
                    </p>
                    <p>{{ formatCurrency(calculateSubtotal()) }}</p>
                  </v-row>
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Total Tax
                    </p>
                    <p>{{ formatCurrency(calculateTotalTax()) }}</p>
                  </v-row>
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Grand Total
                    </p>
                    <p>{{ formatCurrency(calculateGrandTotal()) }}</p>
                  </v-row>
                </v-col>
              </v-col>
            </v-row>

            <v-col cols="12" class="px-0">
              <v-col class="pa-0 ma-0">
                <p class="text-title">Comments</p>
              </v-col>
              <v-col class="px-0">
                <v-textarea
                  v-model="formData.comment"
                  outlined
                  dense
                  auto-grow
                ></v-textarea>
              </v-col>
            </v-col>

            <v-col cols="12" md="12" sm="12">
              <v-card-actions class="mt-10">
                <v-row>
                  <v-col cols="12" md="10" xs="6"></v-col>
                  <v-col cols="12" md="2" xs="6">
                    <v-btn
                      v-if="hasPermission('purchase_order.edit')"
                      large
                      color="#00B3FF"
                      dark
                      depressed
                      block
                      @click="submitForm"
                    >
                      Update PO
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-col>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import ItemsTable from "@/components/purchaseorder/ItemsTable.vue";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import Alert from "@/components/shared/Alert";
import { PurchaseOrderService } from "@/services/purchaseorder-service.js";
import { checkError } from "@/config/errors.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "PurchaseOrderEditForm",
  components: { ItemsTable, HeaderDividerBlock, Alert },
  props: {
    formData: { type: Object, required: true },
    uuid: { type: String, required: false },
    proploading: { type: Boolean, required: true },
  },
  data() {
    return {
      loading: this.proploading,
      valid: false,
      products: [],
      errors: null,
      suppliers: [],
      requisitions: [],
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },

  async created() {
    await this.fetchSuppliersRequisitions();
  },

  methods: {
    async fetchSuppliersRequisitions() {
      this.loading = true;
      try {
        const [suppliersResponse, requisitionResponse] = await Promise.all([
          PurchaseOrderService.getSuppliers(),
          PurchaseOrderService.getRequisitions(),
        ]);
        this.suppliers = suppliersResponse.data;
        this.requisitions = requisitionResponse.data.data;
      } catch (error) {
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },

    async fetchRequisitionProducts(requisitionId) {
      this.loading = true;
      try {
        const response = await PurchaseOrderService.getRequisitionProducts(
          requisitionId
        );
        this.products = response.data.data;
      } catch (error) {
        this.errors = checkError.errorFormatting(error.response.data);
      } finally {
        this.loading = false;
      }
    },

    addItem() {
      this.formData.items.push({
        product_code: "",
        description: "",
        quantity: 0,
        unit_price: 0,
        tax_percent: 0,
      });
    },

    removeItem(index) {
      this.formData.items.splice(index, 1);
    },

    calculateSubtotal() {
      return this.formData.items.reduce(
        (sum, item) =>
          sum +
          (parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0),
        0
      );
    },

    calculateTotalTax() {
      return this.formData.items.reduce(
        (tax, item) =>
          tax +
          ((parseFloat(item.tax_percent) || 0) *
            (parseFloat(item.quantity) || 0) *
            (parseFloat(item.unit_price) || 0)) /
            100,
        0
      );
    },

    calculateGrandTotal() {
      const subtotal = this.calculateSubtotal();
      const tax = this.calculateTotalTax();
      return subtotal + tax;
    },

    // Format currency to 2 decimal places
    formatCurrency(value) {
      const num = parseFloat(value || 0);
      return isNaN(num) ? "0.00" : num.toFixed(2);
    },

    resetForm() {
      this.formData = {
        requisition_id: "",
        supplier_code: "",
        items: [],
        comment: "",
        expected_delivery_date: "",
      };
    },

    async submitForm() {
      console.log("Submitting formData:", this.formData);
      if (!this.$refs.poForm.validate()) return;
      this.loading = true;
      try {
        const itemsToSend = this.formData.items.map((item) => ({
          id: item.product_id,
          product_code: item.product_code,
          quantity: item.quantity,
          tax_percent: item.tax_percent,
          unit_price: item.unit_price,
          description: item.description || "",
        }));

        // Create a shallow copy of formData with filtered items
        const formDataToSend = {
          ...this.formData,
          items: itemsToSend,
        };
        console.log("Data being sent to API:", formDataToSend);
        await PurchaseOrderService.updatePurchaseOrder(
          this.uuid,
          formDataToSend
        );
        this.$router.push({ name: "PurchaseOrderList" });
      } catch (error) {
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },

    onDismissed() {
      this.errors = null;
    },
  },
};
</script>
